import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  fetchNewsArticleById,
  fetchNewsArticles,
  getArticlePrimaryImage,
} from '../../api/newsArticleApi'
import RelatedNews from '../Sliders/RelatedNews'

const NewsArticleDetail = () => {
  const params = useParams()
  const articleId = params?.id

  const [article, setArticle] = useState(null)
  const [relatedArticles, setRelatedArticles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false

    async function loadArticle() {
      if (!articleId) {
        setLoading(false)
        setError('Article not found.')
        return
      }

      setLoading(true)
      setError('')

      try {
        const [articleData, allArticles] = await Promise.all([
          fetchNewsArticleById(articleId),
          fetchNewsArticles(),
        ])

        if (cancelled) return

        setArticle(articleData)
        setRelatedArticles(
          allArticles.filter(
            (item) =>
              item.article_id !== articleData.article_id && Number(item.status) === 1
          )
        )
      } catch (err) {
        if (!cancelled) {
          setArticle(null)
          setRelatedArticles([])
          setError(err.message || 'Article not found.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadArticle()
    return () => {
      cancelled = true
    }
  }, [articleId])

  const imageUrl = useMemo(() => getArticlePrimaryImage(article), [article])

  if (loading) {
    return (
      <section className="space-top">
        <div className="container">
          <p className="text-center py-5">Loading article...</p>
        </div>
      </section>
    )
  }

  if (!article || error) {
    return (
      <section className="space-top">
        <div className="container">
          <p className="text-center py-5">{error || 'Article not found.'}</p>
        </div>
      </section>
    )
  }

  return (
    <>
      <section className="space-top">
        <div className="container">
          <h2 className="text-lora font-semibold mb-4">
            {article.provider_name || article.title}
          </h2>
          <div className="d-flex flex-wrap align-items-center gap-3 mb-4 text-gray-600">
            {article.publication_name && <span>{article.publication_name}</span>}
            {article.publication_date && <span>{article.publication_date}</span>}
          </div>
          {imageUrl && (
            <div className="w-100 rounded-md mb-4">
              <img src={imageUrl} alt={article.title} className="img-fluid rounded-md w-100" />
            </div>
          )}
          <div className="detail-content">
            {article.short_description && <p>{article.short_description}</p>}
            {article.context && <p className="text-gray-600">{article.context}</p>}
            {article.media_link && (
              <p>
                <a href={article.media_link} target="_blank" rel="noopener noreferrer">
                  Read full article
                </a>
              </p>
            )}
          </div>
        </div>
      </section>
      {relatedArticles.length > 0 && (
        <section className="space-top">
          <div className="container">
            <h5 className="lg:text-[24px] text-gray-900 font-semibold mb-3 text-lora">
              You May Also Like
            </h5>
            <div className="row">
              <RelatedNews relatedArticles={relatedArticles} />
            </div>
          </div>
        </section>
      )}
    </>
  )
}

export default NewsArticleDetail
